/**
 * Collapse and "Show more" state for the coding rail's project tree.
 *
 * Two states with deliberately different lifetimes. Which projects are
 * collapsed is a preference — it survives reloads, because re-collapsing the
 * same four repositories on every visit is exactly the kind of small tax that
 * makes a rail feel hostile. Which are expanded past the first five is a
 * momentary act of looking, so it resets, and the rail returns to a scannable
 * height on its own.
 *
 * @module hooks/use-project-tree-state
 */

import { useCallback, useState } from "react"

/** Scoped per app, so two apps do not fight over one set of preferences. */
function storageKey(appId: string | undefined): string {
  return `coding:collapsed-projects:${appId ?? "none"}`
}

function readCollapsed(appId: string | undefined): Set<string> {
  // Storage can throw (Safari private mode, disabled cookies) and can hold
  // anything a previous version wrote. A rail that fails to render because a
  // preference is malformed is far worse than one that forgets the preference.
  try {
    const raw = window.localStorage.getItem(storageKey(appId))
    if (raw === null) return new Set()
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((entry): entry is string => typeof entry === "string"))
  } catch {
    return new Set()
  }
}

export interface ProjectTreeState {
  isCollapsed: (key: string) => boolean
  toggleCollapsed: (key: string) => void
  isExpanded: (key: string) => boolean
  toggleExpanded: (key: string) => void
}

export function useProjectTreeState(appId: string | undefined): ProjectTreeState {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => readCollapsed(appId))
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set<string>())

  const toggleCollapsed = useCallback(
    (key: string) => {
      setCollapsed((current) => {
        const next = new Set(current)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        try {
          window.localStorage.setItem(storageKey(appId), JSON.stringify([...next]))
        } catch {
          // Preference lost, tree still correct for this session.
        }
        return next
      })
    },
    [appId],
  )

  const toggleExpanded = useCallback((key: string) => {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  return {
    isCollapsed: useCallback((key: string) => collapsed.has(key), [collapsed]),
    toggleCollapsed,
    isExpanded: useCallback((key: string) => expanded.has(key), [expanded]),
    toggleExpanded,
  }
}
